/* The guest's bill — ONE template for the web app and the till, so a bill
   reads the same whichever screen printed it. Pure functions only (no
   imports): windows_pos/src/lib/billPrint.js is a byte-for-byte copy of this
   file; change both together.

   computeTaxLines mirrors the till's cart maths: non-compound percent taxes
   apply to the subtotal, compound ones to the running tax-inclusive total
   (the server returns compound taxes last), FLAT taxes add a fixed amount. */
export function computeTaxLines(subtotal, taxes) {
  const base = Number(subtotal) || 0;
  let running = base;
  const lines = [];
  for (const t of taxes || []) {
    let amount;
    if (t.rate_type === 'FLAT') amount = Number(t.rate) || 0;
    else amount = (t.is_compound ? running : base) * ((Number(t.rate) || 0) / 100);
    running += amount;
    lines.push({
      id: t.id,
      label: t.rate_type === 'FLAT' ? t.name : `${t.name} ${Number(t.rate)}%`,
      amount,
    });
  }
  return { lines, total: running };
}

/* Everything a bill shows, worked out once from the order the server returns
   (RestaurantOrderSerializer: lines with prices, advance_amount/status) and
   the taxes Settings → Tax applies today. HELD lines were never fired, so
   they are not on the bill. */
export function buildBill(order, taxes) {
  const items = (order?.items || [])
    .filter((it) => it.status !== 'HELD')
    .map((it) => {
      const qty = Number(it.quantity) || 0;
      const unit = Number(it.unit_price) || 0;
      const total = it.total_price != null ? Number(it.total_price) : qty * unit;
      const names = (arr) => (arr || [])
        .map((a) => (typeof a === 'string' ? a : a && a.name)).filter(Boolean);
      return {
        name: it.item_name || it.name || 'Item',
        qty, unit, total,
        addons: names(it.addons),
        complimentary: names(it.complimentary),
      };
    });
  const subtotal = items.length
    ? items.reduce((s, it) => s + it.total, 0)
    : Number(order?.total_amount) || 0;
  const { lines: taxLines, total: grossTotal } = computeTaxLines(subtotal, taxes);
  const advance = Number(order?.advance_amount) || 0;
  /* Reward deductions the server recorded against this order (detail fetches
     only — the list endpoints don't carry the block, and a bill built from a
     list row simply shows none). Vouchers and points come off as discounts,
     a gift card as payment; the due subtracts all three. */
  const L = order?.loyalty || null;
  const rewards = {
    vouchers: (L?.vouchers || []).map((v) => ({
      code: v.code, title: v.title, kind: v.kind, value: Number(v.value) || 0,
    })),
    voucherDiscount: Number(L?.voucher_discount) || 0,
    pointsValue: Number(L?.points_value) || 0,
    giftCardPaid: Number(L?.gift_card_paid) || 0,
  };
  rewards.total = rewards.voucherDiscount + rewards.pointsValue + rewards.giftCardPaid;
  return {
    items, subtotal, taxLines, grossTotal, advance, rewards,
    advanceStatus: order?.advance_status || null,
    due: Math.max(0, grossTotal - advance - rewards.total),
  };
}

const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

/* A self-contained HTML document sized for an 80 mm receipt printer; on an
   A4 printer it comes out as a narrow centred slip. `money` formats amounts
   the way the calling app does (currency symbol and all). */
export function buildBillHtml({
  restaurantName, locationName, locationPhone, tableCode, order, bill, money, paid,
  printedAt = new Date(),
}) {
  const fmt = money || ((n) => (Number(n) || 0).toFixed(2));
  const when = (iso) => {
    const d = iso ? new Date(iso) : null;
    return d && !Number.isNaN(d.getTime()) ? d.toLocaleString() : '';
  };
  const rows = bill.items.map((it) => `
    <tr><td class="q">${it.qty}×</td><td>${esc(it.name)}${
  it.addons.length ? `<div class="sub">+ ${esc(it.addons.join(', '))}</div>` : ''}</td>
    <td class="a">${esc(fmt(it.total))}</td></tr>`).join('');
  const taxRows = bill.taxLines.map((t) => `
    <tr><td colspan="2">${esc(t.label)}</td><td class="a">${esc(fmt(t.amount))}</td></tr>`).join('');
  const meta = [
    `Table ${esc(tableCode || order.table_code || '')}`,
    order.party_size ? `${order.party_size} pax` : '',
    order.customer_name ? esc(order.customer_name) : '',
  ].filter(Boolean).join(' · ');
  return `<!doctype html><html><head><meta charset="utf-8"><title>Bill ${esc(order.order_number)}</title>
<style>
  @page { margin: 6mm; }
  body { font: 12px/1.35 "Segoe UI", Arial, sans-serif; color: #111; margin: 0 auto; max-width: 76mm; }
  h1 { font-size: 16px; margin: 0; text-align: center; }
  .c { text-align: center; } .m { color: #555; font-size: 11px; }
  table { width: 100%; border-collapse: collapse; margin-top: 6px; }
  td { padding: 2px 0; vertical-align: top; }
  td.q { width: 26px; white-space: nowrap; } td.a { text-align: right; white-space: nowrap; }
  .sub { color: #555; font-size: 10.5px; }
  tr.line td { border-top: 1px dashed #999; padding-top: 4px; }
  tr.total td { font-weight: 700; font-size: 14px; border-top: 1px solid #111; padding-top: 4px; }
  .paid { margin-top: 8px; text-align: center; font-weight: 800; letter-spacing: .08em; border: 2px solid #111; padding: 4px; }
  .foot { margin-top: 10px; text-align: center; font-size: 11px; color: #555; }
</style></head><body>
<h1>${esc(restaurantName || 'RestoBOXX')}</h1>
${locationName ? `<div class="c m">${esc(locationName)}${locationPhone ? ` · ${esc(locationPhone)}` : ''}</div>` : ''}
<div class="c" style="margin-top:6px"><b>BILL</b> · ${esc(order.order_number)}</div>
<div class="c m">${meta}</div>
<div class="c m">Ordered ${esc(when(order.created_at))}</div>
<table>${rows}
<tr class="line"><td colspan="2">Subtotal</td><td class="a">${esc(fmt(bill.subtotal))}</td></tr>${taxRows}
${bill.taxLines.length ? `<tr><td colspan="2">Total</td><td class="a">${esc(fmt(bill.grossTotal))}</td></tr>` : ''}
${bill.advance > 0 ? `<tr><td colspan="2">Booking advance${bill.advanceStatus === 'APPLIED' ? ' (adjusted)' : ''}</td><td class="a">− ${esc(fmt(bill.advance))}</td></tr>` : ''}
${(bill.rewards?.vouchers || []).filter((v) => v.value > 0).map((v) => `<tr><td colspan="2">Voucher · ${esc(v.title)}<div class="sub">${esc(v.code)}</div></td><td class="a">− ${esc(fmt(v.value))}</td></tr>`).join('')}
${bill.rewards?.pointsValue > 0 ? `<tr><td colspan="2">Points redeemed</td><td class="a">− ${esc(fmt(bill.rewards.pointsValue))}</td></tr>` : ''}
${bill.rewards?.giftCardPaid > 0 ? `<tr><td colspan="2">Gift card</td><td class="a">− ${esc(fmt(bill.rewards.giftCardPaid))}</td></tr>` : ''}
<tr class="total"><td colspan="2">${paid ? 'PAID' : 'Amount due'}</td><td class="a">${esc(fmt(bill.due))}</td></tr>
</table>
${paid ? '<div class="paid">PAID — THANK YOU</div>' : ''}
<div class="foot">Printed ${esc(printedAt.toLocaleString())}</div>
</body></html>`;
}
